/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  maxWidth, retina,
  primaryHeadingColor, secondaryFont, wMBold,
} from 'Utils/utilsStyle';
import { useDispatch, useSelector } from 'react-redux';
import { actions as SRIActions } from 'Slice/SRISlice';
import {
  selectUserScore,
  selectUserScoreGroup,
  selectCompanyGraph,
  selectPeopleGraph,
  selectSessionGraph,
  selectEmailOpenGraph,
  selectArticleGraph,
  selectPeopleGraphLoading,
  selectCompanyGraphLoading,
  selectArticleGraphLoading,
  selectEmailOpenGraphLoading,
  selectSessionGraphLoading,
  userScoreLoading,
  selectOverviewFilter,
  selectSRISelectedUserId,
  selectSRISelectedUser,
  selectComplianceDataLoading,
  selectComplianceData,
  selectCompanyOverviewEngagement,
  selectPersonOverviewEngagement,
  selectAlertOverviewEngagement,
  selectPeopleLoading,
  selectCompanyLoading,
  selectAlertsLoading,
  selectCompanyFetchMoreLoading,
  selectPeopleFetchMoreLoading,
} from 'Selector/SRISelector';
import {
  FiltersTypeObject,
  SRIFilters, SRIOverviewData, SRIUserScore,
} from 'Types/SRITypes';
import { SRI } from 'Utils/SRIConstants';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { getCurrentTime, getTimezoneInGMT, valueToObject } from 'Utils/UtilityFunctions';
import { selectUserCreationDate } from 'Selector/GlobalSearchContentSelector';
import { SRI_FILTER_TYPES_OVERVIEW } from 'Utils/Constants';
import ChartsOverlay from './ChartOverlay';
import OverviewEngagementCard from './OverviewEngagement';
import UserScoreCard from './UserScoreCard';
import ComplianceTable from './ComplianceDashboard';

const Wrapper = styled.div`
  .title {
    font-size: 25px;
    font-weight: ${wMBold};
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: ${primaryHeadingColor};
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 22px;
    }
  }
  .tabs-item {
    cursor: pointer;
  }
  .no-data-holder {
    font-size: 20px;
    height: 250px;
    width: 100%;
    margin: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: ${wMBold};
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: ${primaryHeadingColor};
  }
`;

const OverViewTab = function OverViewTab() {
  const sriSelectedUserId = useSelector(selectSRISelectedUserId);
  const sriSelectedUser = useSelector(selectSRISelectedUser);

  const companyLoading = useSelector(selectCompanyLoading);
  const personLoading = useSelector(selectPeopleLoading);
  const triggersLoading = useSelector(selectAlertsLoading);

  const fetchMoreCompanyLoading = useSelector(selectCompanyFetchMoreLoading);
  const fetchMorePersonLoading = useSelector(selectPeopleFetchMoreLoading);

  const userScore: SRIUserScore = useSelector(selectUserScore);
  const userScoreGraph = useSelector(selectUserScoreGroup);
  const dispatch = useDispatch();
  const OverviewCompanyEngagement: SRIOverviewData[] = useSelector(selectCompanyOverviewEngagement);
  const OverviewPersonEngagement: SRIOverviewData[] = useSelector(selectPersonOverviewEngagement);
  const OverviewAlertEngagement: SRIOverviewData[] = useSelector(selectAlertOverviewEngagement);
  const companyGraph = useSelector(selectCompanyGraph);
  const peopleGraph = useSelector(selectPeopleGraph);
  const articleGraph = useSelector(selectArticleGraph);
  const sessionGraph = useSelector(selectSessionGraph);
  const emailOpenGraph = useSelector(selectEmailOpenGraph);
  const peopleGraphLoading = useSelector(selectPeopleGraphLoading);
  const companyGraphLoading = useSelector(selectCompanyGraphLoading);
  const articleGraphLoading = useSelector(selectArticleGraphLoading);
  const sessionGraphLoading = useSelector(selectSessionGraphLoading);
  const emailOpenGraphLoading = useSelector(selectEmailOpenGraphLoading);
  const userScoreLoadingSelector = useSelector(userScoreLoading);
  const SRIfilters: SRIFilters = useSelector(selectOverviewFilter);
  const complianceData = useSelector(selectComplianceData);
  const complianceDataLoading = useSelector(selectComplianceDataLoading);
  const userCreationDate = useSelector(selectUserCreationDate);
  const timezone = getTimezoneInGMT();

  const getFormObject = (
    { type, sections }: FiltersTypeObject,
    filterType?: string,
    offset?: number,
  ) => ({
    __TYPE__: type,
    ...(filterType ? { filterType } : {}),
    sections,
    filters: [{ type, ...SRIfilters }],
    ...(sriSelectedUserId ? { user_id: sriSelectedUserId } : {}),
    ...(offset ? { offset } : {}),
  });

  useEffect(() => {
    const startTime = getCurrentTime();
    dispatch(flurryActions.callFlurryEvent(valueToObject('sri_overview_loaded', startTime)));
  }, []);

  useEffect(() => {
    if (!complianceData?.length) {
      dispatch(SRIActions.getComplianceData({
        offset: 0,
        limit: 20,
        timezone,
      }));
    }
  }, []);

  useEffect(() => {
    const hasValidDateRange = SRIfilters.start_date && SRIfilters.end_date && SRIfilters.start_date !== 'Invalid date';

    if (hasValidDateRange) {
      dispatch(SRIActions.getSRIUserScore(getFormObject(SRI.USER_SCORE)));
      dispatch(SRIActions.getSRIOverviewCompanyViews(getFormObject(SRI.COMPANY_VIEWS)));
      dispatch(SRIActions.getSRIOverviewPeopleViews(getFormObject(SRI.PEOPLE_VIEWS)));
      dispatch(SRIActions.getSRIOverviewArticlsRead(getFormObject(SRI.ARTICLES_READ)));
      dispatch(SRIActions.getSRIOverviewPlatformSessions(getFormObject(SRI.PLATFORM_SESSIONS)));
      dispatch(SRIActions.getSRIOverviewEmailsOpened(getFormObject(SRI.EMAILS_OPENED)));

      SRI_FILTER_TYPES_OVERVIEW.forEach((filterType: string) => {
        dispatch(SRIActions.getSRIOverviewEngagement(getFormObject(SRI.ENGAGEMENT, filterType)));
      });
    }
  }, [SRIfilters, sriSelectedUserId, userCreationDate]);
  const loaders = [companyGraphLoading, peopleGraphLoading,
    articleGraphLoading, sessionGraphLoading, emailOpenGraphLoading];

  const fetchMore = (filterType: string) => {
    let finalOffset = OverviewCompanyEngagement[0]?.results.length;
    if (filterType === 'person') {
      finalOffset = OverviewPersonEngagement[0]?.results.length;
    } else if (filterType === 'alert') {
      finalOffset = OverviewAlertEngagement[0]?.results.length;
    }
    dispatch(SRIActions.getSRIOverviewEngagement(getFormObject(
      SRI.ENGAGEMENT,
      filterType,
      finalOffset,
    )));
  };
  return (
    <Wrapper>
      <UserScoreCard
        loading={userScoreLoadingSelector}
        userScore={sriSelectedUserId
          ? sriSelectedUser?.userScore : userScore}
        userScoreGraph={sriSelectedUserId ? sriSelectedUser?.userScoreGraph : userScoreGraph}
        title={userScore.section_name}
      />
      {!complianceData?.length && !complianceDataLoading ? null : (
        <ComplianceTable
          complianceData={complianceData}
          complianceDataLoading={complianceDataLoading}
        />
      )}
      <OverviewEngagementCard
        companyLoading={companyLoading}
        personLoading={personLoading}
        triggersLoading={triggersLoading}
        fetchMoreCompanyLoading={fetchMoreCompanyLoading}
        fetchMorePersonLoading={fetchMorePersonLoading}
        fetchMore={fetchMore}
        overviewEngagementCompanies={sriSelectedUserId
          ? sriSelectedUser?.overviewCompanyEngagement : OverviewCompanyEngagement}
        overviewEngagementPersons={sriSelectedUserId
          ? sriSelectedUser?.overviewPersonEngagement : OverviewPersonEngagement}
        overviewEngagementTriggers={sriSelectedUserId
          ? sriSelectedUser?.overviewAlertEngagement : OverviewAlertEngagement}
      />

      {[...(sriSelectedUserId ? [sriSelectedUser.companyViewGraph,
      sriSelectedUser.peopleViewGraph, sriSelectedUser.articleViewGraph,
      sriSelectedUser.sessionViewGraph, sriSelectedUser.emailOpenGraph]
        : [companyGraph, peopleGraph,
          articleGraph, sessionGraph,
          emailOpenGraph])].map(
            (graph, index) => (
              <ChartsOverlay loading={loaders[index]} data={graph} />
            ),
          )}
    </Wrapper>
  );
};

export default OverViewTab;
